import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faEdit, 
  faTrash, 
  faHome,
  faBuilding,
  faMapMarkerAlt,
  faCalendarAlt,
  faBed,
  faBath,
  faRulerCombined,
  faLayerGroup,
  faEye,
  faSignOutAlt,
  faFileContract,
  faMoneyBillWave,
  faUser,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { propertyService } from '../../services/propertyService';
import { contractService } from '../../services/contractService';
import { formatCurrency } from '../../utils/currencyFormatter';

const PropertyUnitDetails = () => {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [unit, setUnit] = useState(null);
  const [group, setGroup] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchUnitDetails = async () => {
      try {
        setLoading(true);
        console.log('=== PROPERTY UNIT DETAILS DEBUG ===');
        console.log('Fetching unit details for unitId:', unitId);
        console.log('Environment:', process.env.NODE_ENV);
        console.log('Firebase config:', process.env.REACT_APP_FIREBASE_PROJECT_ID);
        
        // Fetch unit data first
        console.log('Fetching unit data...');
        const unitData = await propertyService.getPropertyUnitById(unitId);
        console.log('Unit data fetched successfully:', unitData);
        
        // Fetch contracts data
        console.log('Fetching contracts data...');
        const contractsData = await contractService.getContractsByPropertyUnit(unitId);
        console.log('Contracts data fetched successfully:', contractsData);
        console.log('Contracts count:', contractsData.length);
        
        // Test: Try to fetch all contracts to see if the service is working
        try {
          console.log('Testing: Fetching all contracts...');
          const allContracts = await contractService.getContractsByCompany(unitData.companyId || 'test');
          console.log('All contracts test result:', allContracts.length, 'contracts found');
        } catch (testError) {
          console.log('All contracts test failed:', testError.message);
        }
        
        setUnit(unitData);
        setContracts(contractsData);
        
        // Fetch the property group details
        if (unitData.groupId) {
          console.log('Fetching group data for groupId:', unitData.groupId);
          const groupData = await propertyService.getPropertyGroupById(unitData.groupId);
          console.log('Group data fetched successfully:', groupData);
          setGroup(groupData);
        }
        
        console.log('=== END DEBUG ===');
      } catch (error) {
        console.error('=== ERROR IN PROPERTY UNIT DETAILS ===');
        console.error('Error fetching unit details:', error);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        console.error('=== END ERROR ===');
        setError('Failed to load property unit details: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    if (unitId) {
      fetchUnitDetails();
    }
  }, [unitId]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleDeleteUnit = async () => {
    try {
      setDeleting(true);
      await propertyService.deletePropertyUnit(unitId);
      navigate(`/manager/property-groups/${unit.groupId}`);
    } catch (error) {
      console.error('Error deleting unit:', error);
      setError('Failed to delete property unit');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { variant: 'success', text: 'Available' },
      occupied: { variant: 'warning', text: 'Occupied' },
      maintenance: { variant: 'danger', text: 'Maintenance' },
      reserved: { variant: 'info', text: 'Reserved' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getTypeIcon = (type) => {
    const iconMap = {
      apartment: faHome,
      villa: faHome,
      penthouse: faHome,
      studio: faHome,
      townhouse: faHome
    };
    return iconMap[type] || faHome;
  };

  const getTypeColor = (type) => {
    const colorMap = {
      apartment: 'primary',
      villa: 'success',
      penthouse: 'warning',
      studio: 'info',
      townhouse: 'secondary'
    };
    return colorMap[type] || 'secondary';
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <Alert variant="danger">
          {error}
          <Button 
            variant="outline-danger" 
            className="ms-3" 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Alert>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="container-fluid">
        <Alert variant="warning">
          Property unit not found
          <Button 
            variant="outline-warning" 
            className="ms-3" 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate(`/manager/property-groups/${unit.groupId}`)}
            className="me-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Group
          </Button>
          <div>
            <h2 className="mb-1">{unit.unitNumber}</h2>
            <p className="text-muted mb-0">
              <FontAwesomeIcon icon={faBuilding} className="me-1" />
              {group?.name} • {group?.location}
            </p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button as={Link} to={`/manager/units/${unitId}/edit`} variant="primary">
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Unit
          </Button>
          <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Delete Unit
          </Button>
          <Button variant="outline-danger" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
            Logout
          </Button>
        </div>
      </div>

      <Row>
        <Col lg={8}>
          {/* Unit Information */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={getTypeIcon(unit.unitType)} className={`text-${getTypeColor(unit.unitType)} me-2`} />
                Property Unit Details
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Unit Number</label>
                    <p className="mb-0 fw-bold">{unit.unitNumber}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Unit Type</label>
                    <p className="mb-0">
                      <Badge bg={getTypeColor(unit.unitType)} className="text-capitalize">
                        {unit.unitType}
                      </Badge>
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Status</label>
                    <p className="mb-0">{getStatusBadge(unit.status)}</p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Floor</label>
                    <p className="mb-0">
                      <FontAwesomeIcon icon={faLayerGroup} className="me-1" />
                      {unit.floor || 'Ground Floor'}
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Bedrooms</label>
                    <p className="mb-0">
                      <FontAwesomeIcon icon={faBed} className="me-1" />
                      {unit.bedrooms || 'Studio'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Bathrooms</label>
                    <p className="mb-0">
                      <FontAwesomeIcon icon={faBath} className="me-1" />
                      {unit.bathrooms || 'N/A'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Area</label>
                    <p className="mb-0">
                      <FontAwesomeIcon icon={faRulerCombined} className="me-1" />
                      {unit.area ? `${unit.area} sq ft` : 'N/A'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="form-label text-muted small">Created</label>
                    <p className="mb-0">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {unit.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </p>
                  </div>
                </Col>
              </Row>
              
              {unit.description && (
                <div className="mt-3">
                  <label className="form-label text-muted small">Description</label>
                  <p className="mb-0">{unit.description}</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Property Group Information */}
          {group && (
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0">
                <h5 className="mb-0">
                  <FontAwesomeIcon icon={faBuilding} className="text-primary me-2" />
                  Property Group Information
                </h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label text-muted small">Group Name</label>
                      <p className="mb-0 fw-bold">{group.name}</p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label text-muted small">Group Type</label>
                      <p className="mb-0">
                        <Badge bg="primary" className="text-capitalize">
                          {group.type}
                        </Badge>
                      </p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <label className="form-label text-muted small">Location</label>
                      <p className="mb-0">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                        {group.location}
                      </p>
                    </div>
                    <div className="mb-3">
                      <label className="form-label text-muted small">Group Status</label>
                      <p className="mb-0">
                        <Badge bg="success">Active</Badge>
                      </p>
                    </div>
                  </Col>
                </Row>
                <div className="mt-3">
                  <Button 
                    as={Link} 
                    to={`/manager/property-groups/${group.id}`} 
                    variant="outline-primary"
                  >
                    <FontAwesomeIcon icon={faEye} className="me-2" />
                    View All Units in Group
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Contracts */}
          <Card className="border-0 shadow-sm mt-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faFileContract} className="text-primary me-2" />
                Contracts ({contracts.length})
              </h5>
            </Card.Header>
            <Card.Body>
              {console.log('Rendering contracts, count:', contracts.length, 'contracts:', contracts)}
              {contracts.length === 0 ? (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faFileContract} className="text-muted mb-3" size="3x" />
                  <h6>No contracts found</h6>
                  <p className="text-muted">This unit has no active contracts.</p>
                  <Button 
                    variant="primary" 
                    onClick={() => navigate('/manager/contracts/create', { state: { propertyUnitId: unitId } })}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Create Contract
                  </Button>
                </div>
              ) : (
                <div>
                  {contracts.map((contract) => (
                    <Card key={contract.id} className="mb-3 border">
                      <Card.Body>
                        <Row>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Tenant:</strong>
                              <div className="text-muted">{contract.tenantId || 'N/A'}</div>
                            </div>
                            <div className="mb-2">
                              <strong>Start Date:</strong>
                              <div className="text-muted">
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                {contract.contractStartDate ? new Date(contract.contractStartDate).toLocaleDateString() : 'N/A'}
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>End Date:</strong>
                              <div className="text-muted">
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                {contract.contractEndDate ? new Date(contract.contractEndDate).toLocaleDateString() : 'N/A'}
                              </div>
                            </div>
                            {contract.contractDuration && (
                              <div className="mb-2">
                                <strong>Duration:</strong>
                                <div className="text-muted">{contract.contractDuration}</div>
                              </div>
                            )}
                          </Col>
                          <Col md={6}>
                            <div className="mb-2">
                              <strong>Monthly Rent:</strong>
                              <div className="text-muted">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                                {formatCurrency(contract.chequeAmount || 0, contract.currency || 'USD')}
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>Total Amount:</strong>
                              <div className="text-muted">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                                {formatCurrency(contract.totalRentalAmount || 0, contract.currency || 'USD')}
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>Status:</strong>
                              <div>
                                <Badge bg={contract.status === 'active' ? 'success' : 'secondary'}>
                                  {contract.status}
                                </Badge>
                              </div>
                            </div>
                            <div className="mb-2">
                              <strong>Contract ID:</strong>
                              <div className="text-muted small">{contract.contractId || contract.id}</div>
                            </div>
                          </Col>
                        </Row>
                        <div className="mt-3">
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={() => navigate(`/manager/contracts/${contract.id}`)}
                          >
                            View Details
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {/* Quick Actions */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">Quick Actions</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                <Button as={Link} to={`/manager/units/${unitId}/edit`} variant="primary">
                  <FontAwesomeIcon icon={faEdit} className="me-2" />
                  Edit Unit
                </Button>
                <Button 
                  variant="success"
                  onClick={() => navigate('/manager/contracts/create', { state: { propertyUnitId: unitId } })}
                >
                  <FontAwesomeIcon icon={faFileContract} className="me-2" />
                  Create Contract
                </Button>
                <Button 
                  as={Link} 
                  to={`/manager/property-groups/${unit.groupId}`} 
                  variant="outline-primary"
                >
                  <FontAwesomeIcon icon={faBuilding} className="me-2" />
                  Back to Group
                </Button>
                <Button 
                  as={Link} 
                  to="/dashboard" 
                  variant="outline-secondary"
                >
                  <FontAwesomeIcon icon={faHome} className="me-2" />
                  Dashboard
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Unit Statistics */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <h5 className="mb-0">Unit Information</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Unit Type</span>
                  <Badge bg={getTypeColor(unit.unitType)} className="text-capitalize">
                    {unit.unitType}
                  </Badge>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Status</span>
                  {getStatusBadge(unit.status)}
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Bedrooms</span>
                  <span className="fw-bold">{unit.bedrooms || 'Studio'}</span>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Bathrooms</span>
                  <span className="fw-bold">{unit.bathrooms || 'N/A'}</span>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Area</span>
                  <span className="fw-bold">{unit.area ? `${unit.area} sq ft` : 'N/A'}</span>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Floor</span>
                  <span className="fw-bold">{unit.floor || 'Ground'}</span>
                </div>
              </div>
              <hr />
              <div className="text-center">
                <h4 className="mb-1">{contracts.length}</h4>
                <p className="text-muted mb-0">Total Contracts</p>
              </div>
              {contracts.length > 0 && (
                <>
                  <hr />
                  <div className="text-center">
                    <h4 className="mb-1">
                      {formatCurrency(
                        contracts.reduce((sum, contract) => sum + (contract.chequeAmount || 0), 0),
                        contracts[0]?.currency || 'USD'
                      )}
                    </h4>
                    <p className="text-muted mb-0">Total Monthly Rent</p>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Property Unit</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the property unit <strong>"{unit.unitNumber}"</strong>?</p>
          <p className="text-danger">
            <strong>Warning:</strong> This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteUnit}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Unit'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PropertyUnitDetails; 